import type { FC } from 'react';
import { useEffect } from 'react';

import { Helmet } from 'react-helmet';

import { DashboardEvent } from 'features/dashboard/lib/constants';
import { trackEvent } from 'shared/lib/analytics/trackEvent';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useFeatures } from 'shared/lib/features/useFeatures';
import { getHubspotContainer, pinHubspotLauncherZIndex } from 'shared/lib/hubspot/launcher';

export const HubspotChat: FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { isLoading } = useFeatures();

  // Cap the HubSpot launcher's z-index so an open assistant chat covers it (HubSpot's default sits
  // above everything). Position is left to HubSpot; the Ask Truv button moves beside it instead.
  useEffect(() => {
    if (!(isAuthenticated && import.meta.env.VITE_APP_HUBSPOT_ID)) {
      return;
    }
    let styleObserver: MutationObserver | null = null;

    const attach = (): boolean => {
      const container = getHubspotContainer();
      if (!container) {
        return false;
      }
      pinHubspotLauncherZIndex(container);
      styleObserver = new MutationObserver(() => pinHubspotLauncherZIndex(container));
      styleObserver.observe(container, { attributes: true, attributeFilter: ['style'] });
      return true;
    };

    if (attach()) {
      return () => styleObserver?.disconnect();
    }

    // Container mounts after the HubSpot script loads; watch for it, then stop watching the tree.
    const treeObserver = new MutationObserver(() => {
      if (attach()) {
        treeObserver.disconnect();
      }
    });
    treeObserver.observe(document.body, { childList: true, subtree: true });
    return () => {
      treeObserver.disconnect();
      styleObserver?.disconnect();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleHubspotMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://app.hubspot.com') {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        if (data.type === 'open-change' && data.data?.isOpen === true) {
          trackEvent(DashboardEvent.SUPPORT_CHAT_CLICK, { provider: 'hubspot' });
        }
      } catch {}
    };

    window.hsConversationsSettings = {
      loadImmediately: false,
      identificationToken: user?.hubspot_identification_token,
      identificationEmail: user?.email,
    };

    window.hsConversationsOnReady = [];
    window.hsConversationsOnReady.push(() => {
      if (user && !user?.hubspot_identification_token) {
        console.error(
          'No hubspot identification token from the profile. User is going to be anonymous in Hubspot live chat',
        );
      }

      window.HubSpotConversations?.widget.load();
    });

    window.addEventListener('message', handleHubspotMessage);

    return () => {
      window.removeEventListener('message', handleHubspotMessage);
    };
  }, [user]);

  if (!(isAuthenticated && import.meta.env.VITE_APP_HUBSPOT_ID) || isLoading) {
    return null;
  }

  return (
    <Helmet>
      <script
        async
        defer
        id="hs-script-loader"
        src={`https://js.hs-scripts.com/${import.meta.env.VITE_APP_HUBSPOT_ID}.js`}
      />
    </Helmet>
  );
};
